import {
  mapMyRfiItemToGridRow,
  mapGridRowToExportRecord as mapMyRfiRowToExportRecord,
  sortConfigToApiSortParamsWithFieldMap,
  sortMyRfiItems,
} from "./myRfiGridRowMapper";
import { getVendorRfisAllPagesForExport } from "../api/api-methods";
import { buildExportDataFromAllPages } from "./gridExportAllRecords";

/**
 * Toggle server-side sort for Provider Requested RFIs vs client-side sort on the current page.
 */
export const PROVIDER_REQUESTED_RFIS_USE_SERVER_SORT = true;

/**
 * Column keys → `order_by` for `RFI-Templates/Provider/get_rfis`.
 * Align with backend when enabling server-side sort (same idea as MY_RFI_API_ORDER_BY_FIELD).
 */
// 14029 — `risk` deliberately omitted: enriched client-side via /RiskScoring/summaries.
export const VENDOR_REQUESTED_RFI_API_ORDER_BY_FIELD = {
  sponsor: "sponsor_name",
  template: "services",
  gxp: "gxp",
  /** Server sorts by city/state/country (same as the visible Location column), not street address. */
  location: "location",
  status: "status",
  requestDate: "request_date",
  dueDate: "due_by",
  releasedDate: "initial_release_date",
  completionDate: "release_date",
  cycleTime: "cycle_time",
};

export function sortConfigToVendorRequestedApiSortParams(sortConfig) {
  return sortConfigToApiSortParamsWithFieldMap(
    VENDOR_REQUESTED_RFI_API_ORDER_BY_FIELD,
    sortConfig
  );
}

/**
 * Normalized row for Provider "Requested RFIs" grid (vendor-facing list).
 * Built on top of the shared My RFI mapper so date/GxP/location fields stay consistent.
 *
 * When the backend adds fields, extend `mapVendorRequestedRfiToGridRow` to read them
 * from `item` (e.g. item.auditor_name). Missing keys stay empty strings for display/export.
 */
export function mapVendorRequestedRfiToGridRow(item) {
  const base = mapMyRfiItemToGridRow(item);
  return {
    ...base,
    sponsor: item?.sponsorName ?? "",
  };
}

/** Column order for TableExportMenu (matches VendorRequestedRfiGridTable). */
export const VENDOR_REQUESTED_RFI_EXPORT_HEADERS = [
  "Sponsor",
  "Type",
  "GxP",
  "Template",
  "Location",
  "Status",
  "Request Date",
  "Due Date",
  "Release Date",
  "Completion Date",
  "Cycle Time",
];

/**
 * One export row per API item: same fields as purchaser "My RFIs" export but
 * the first column is Sponsor instead of Vendor, and no Risk Summary: risk
 * scoring is never exposed to vendors.
 * @param {object} item — raw API row
 */
export function mapVendorRequestedItemToExportRecord(item) {
  const row = mapVendorRequestedRfiToGridRow(item);
  const { Vendor, "Risk Summary": riskSummary, ...rest } = mapMyRfiRowToExportRecord(row, null);
  return {
    Sponsor: row.sponsor || "",
    ...rest,
  };
}

/**
 * Export-menu data builder for the provider "My VQQs" grid: fetches every page
 * of the current filter/sort selection. No risk summaries are fetched, since
 * vendors never see risk scoring. Template selections ride in the services id
 * list, matching the grid's own query. The page passes its live filter state
 * in via useGridExportRegistration.
 */
export const buildVendorRequestedRfisExportData = ({
  searchQuery,
  selectedIds,
  serverSortParams,
  sortConfig,
  dueDateFilter,
}) =>
  buildExportDataFromAllPages({
    fetchAllPages: () =>
      getVendorRfisAllPagesForExport({
        searchQuery,
        sponsor: selectedIds.sponsor || [],
        provider: [],
        docType: selectedIds.auditor || [],
        status: selectedIds.status || [],
        services: [
          ...(selectedIds.services || []),
          ...(selectedIds.templates || []),
        ],
        providers: [],
        sortOptions: serverSortParams || {},
        dueDateFilter,
      }),
    refineItems: (items) =>
      PROVIDER_REQUESTED_RFIS_USE_SERVER_SORT
        ? items
        : sortMyRfiItems(items, sortConfig),
    mapRecord: (item) => mapVendorRequestedItemToExportRecord(item),
    headers: VENDOR_REQUESTED_RFI_EXPORT_HEADERS,
  });

